import React, { Suspense, useEffect, useState } from "react";
import {
  Constants,
  useMediaDevice,
  useMeeting,
} from "@videosdk.live/react-sdk";
import useIsMobile from "../utils/useIsMobile";
import { useAppContext } from "../AppContextDef";
import { useTranslation } from "react-i18next";
import ConfirmBox from "../components/ConfirmBox";
import useIsTab from "../utils/useIsTab";
import useDeviceAnalytics from "../utils/useDeviceAnalytics";
import useAuthFetch from "../utils/useAuthFetch";

const Intropage = ({
  setJoined,
  setCurrentPage,
  referenceNo,
  handleChildData,
  onDataSend,
}) => {
  const {
    allDetails,
    setSelectedLanguageCode,
    setJourneyData,
    setJourneyConfig,
    isCameraPermissionAllowed,
    isMicrophonePermissionAllowed,
    isLocationPermissionAllowed,
    setIsCameraPermissionAllowed,
    setIsMicrophonePermissionAllowed,
    setIsLocationPermissionAllowed,
    encryptedText,
    setDeviceData,
    setIsStartingJourneyClick,
    isStartingJourneyClick,
    mediaError,
    setMediaError,
  } = useAppContext();
  const [isDifferentClose, setIsDifferentClose] = useState(false)
  const [open, setOpen] = useState(false);
  const customFetch = useAuthFetch();
  let isMobile = useIsMobile();
  let isTab = useIsTab();
  const [refNo] = useState(referenceNo);
  const [lang, setLang] = useState("");
  const [err, setErr] = useState(false);
  const [openErrorBox, setOpenErrorBox] = useState(false);
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [errMsg, setErrMsg] = useState("");
  let isMobileOrTab = isMobile || isTab;
  const {
    isLoading: loadData,
    latitude,
    longitude,
    browserName,
    deviceName,
    network,
    error,
  } = useDeviceAnalytics();
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const languageMap = {
    English: "en-IN",
    Hindi: "hi-IN",
    Gujarati: "gu-IN",
    Punjabi: "pa-IN",
    Bengali: "bn-IN",
    Marathi: "mr-IN",
    Kannada: "kn-IN",
    Tamil: "ta-IN",
    Telugu: "te-IN",
    Malayalam: "ml-IN",
    Odia: "od-IN",
  };

  const languageNativeMap = {
    English: "English",
    Hindi: "हिंदी",
    Gujarati: "ગુજરાતી",
    Punjabi: "ਪੰਜਾਬੀ",
    Bengali: "বাংলা",
    Marathi: "मराठी",
    Kannada: "ಕನ್ನಡ",
    Tamil: "தமிழ்",
    Telugu: "తెలుగు",
    Malayalam: "മലയാളം",
    Odia: "ଓଡ଼ିଆ",
  };

  const preferredOrder = ["English", "Hindi", "Gujarati", "Marathi"];

  useEffect(() => {
    if (allDetails?.supportedLanguages) {
      const sortedLanguages = allDetails.supportedLanguages
        .filter((lang) => languageMap[lang])
        .sort((a, b) => {
          const indexA = preferredOrder.indexOf(a);
          const indexB = preferredOrder.indexOf(b);

          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB; // Maintain preferred order
          }
          if (indexA !== -1) return -1; // Place preferred languages first
          if (indexB !== -1) return 1;
          return a.localeCompare(b); // Default sorting for others
        });

      setSupportedLanguages(sortedLanguages);
    }
  }, [allDetails]);

  useEffect(() => {
    if (!loadData) {
      if (
        !latitude ||
        latitude === undefined ||
        !longitude ||
        latitude === undefined ||
        !browserName ||
        !deviceName ||
        !network ||
        error
      ) {
        setOpenErrorBox(true);
      } else {
        setDeviceData({
          latitude,
          longitude,
          browserName,
          deviceName,
          network,
        });
      }
    }
  }, [loadData, latitude, longitude, browserName, deviceName, network, error]);

  const handleLanguageChange = (e) => {
    const selectedLang = e.target.value;
    setLang(selectedLang);
    const selectedCode = languageMap[selectedLang];
    changeLanguage(selectedCode);
    onDataSend(selectedCode);
    setSelectedLanguageCode(selectedCode);
  };

  const { join } = useMeeting({
    onMeetingJoined,
  });

  function onMeetingJoined() {
    setJoined("JOINED");
    // setCurrentPage("PersonalDetails");
  }

  const handleValidation = (lang) => {
    let isValid = true;

    if (!lang.lang) {
      isValid = false;
      setErr(true);
      return false;
    } else {
      setErr(false);
    }

    return isValid;
  };

  const startJourney = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/v1/journey/start`;
      const method = "POST";
      const headers = {
        authorization: `${allDetails?.authToken}`,
        "Content-Type": "application/json",
      };
      const bodyContent = {
        encryptedText: decodeURIComponent(encryptedText),
        videoSDKRoomId: `${allDetails?.videoSDKRoomId}`,
        agentCode: `${allDetails?.iciciApplicationDetails?.fsc_um_code}`,
        language: lang,
        templateId: allDetails?.templateId,
        navigatorInfo : navigator.userAgent
      };
      const { response, status, message } = await customFetch(
        url,
        method,
        headers,
        bodyContent
      );

      if (status === 201) {
        setJourneyData(response?.journey);
        setJourneyConfig(response?.journeyConfig);
        handleChildData(response.data);
        return true;
      }
      else if (status === 400) {
        setOpen(true)
        setErrMsg(message)
        setIsDifferentClose(true)
        return false;
      }
      else {
        setOpen(true);
        // setErrMsg(message);
        setErrMsg(
          `${t(
            "Oops! Some error occurred. Please reach out to our sales representative or write to us at vidassure@iciciprulife.com [ERR099]"
          )}`
        );
        return false;
      }
    } catch (e) {
      setOpen(true);
      // setErrMsg(message);
      setErrMsg(
        `${t(
          "Oops! Some error occurred. Please reach out to our sales representative or write to us at vidassure@iciciprulife.com [ERR099]"
        )}`
      );
      return false;
    }
  };

  const joinMeeting = () => {
    join();
    setJoined("JOINING");
  };

  const { checkPermissions, requestPermission } = useMediaDevice();

  const checkMediaPermission = async () => {
    try {
      const checkAudioVideoPermission = await checkPermissions();
      const cameraPermissionAllowed = checkAudioVideoPermission.get(
        Constants.permission.VIDEO
      );
      const microphonePermissionAllowed = checkAudioVideoPermission.get(
        Constants.permission.AUDIO
      );

      const locationStatus = await navigator.permissions.query({
        name: "geolocation",
      });

      if (locationStatus.state === "granted") {
        setIsLocationPermissionAllowed(true);
      } else if (locationStatus.state === "denied") {
        setOpen(true);
        setErrMsg(t("Location permission is denied by user"));
        setIsLocationPermissionAllowed(false);
      } else {
        if (!navigator.geolocation) {
          console.error(t("Geolocation is not supported by this browser."));
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            setIsLocationPermissionAllowed(true);
          },
          (error) => {
            setOpen(true);
            setErrMsg(
              isMobile
                ? `${t(
                  "Please allow location service from browser settings to continue."
                )}`
                : `${t(
                  "Error accessing location: User denied Geolocation. [ERR001]"
                )}`
            );
            setIsLocationPermissionAllowed(false);
            console.error(
              "Error accessing location: User denied Geolocation. [ERR001]"
            );
          }
        );
      }

      setIsCameraPermissionAllowed(cameraPermissionAllowed);
      setIsMicrophonePermissionAllowed(microphonePermissionAllowed);

      if (!microphonePermissionAllowed) {
        await requestAudioVideoPermission(Constants.permission.AUDIO);
      }

      if (!cameraPermissionAllowed) {
        await requestAudioVideoPermission(Constants.permission.VIDEO);
      }

      try {
        await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        setMediaError(null); // No error if permissions granted
      } catch (err) {
        setOpen(true);
        setMediaError(
          `${t(
            "Unable to access camera or microphone. Please ensure you have all the necessary permissions. [ERR004]"
          )}`
        );
        console.error("Error accessing media devices:", err);
      }
    } catch (error) {
      // For Firefox, it will request audio and video simultaneously.
      await requestAudioVideoPermission(Constants.permission.AUDIO_AND_VIDEO);
      console.log(error);
    }
  };

  const requestAudioVideoPermission = async (mediaType) => {
    try {
      const permission = await requestPermission(mediaType);

      // For Video
      if (mediaType === Constants.permission.VIDEO) {
        const isVideoAllowed = permission.get(Constants.permission.VIDEO);
        setIsCameraPermissionAllowed(isVideoAllowed);
      }

      // For Audio
      if (mediaType === Constants.permission.AUDIO) {
        const isAudioAllowed = permission.get(Constants.permission.AUDIO);
        setIsMicrophonePermissionAllowed(isAudioAllowed);
      }

      if (mediaType === Constants.permission.AUDIO_AND_VIDEO) {
        const isVideoAllowed = permission.get(Constants.permission.VIDEO);
        setIsCameraPermissionAllowed(isVideoAllowed);
        const isAudioAllowed = permission.get(Constants.permission.AUDIO);
        setIsMicrophonePermissionAllowed(isAudioAllowed);
      }

      // For Location
      if (!navigator.geolocation) {
        console.error("Geolocation is not supported by this browser.");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setIsLocationPermissionAllowed(true);
        },
        (error) => {
          setOpen(true);
          setErrMsg(
            isMobile
              ? `${t(
                "Please allow location service from browser settings to continue."
              )}`
              : `${t(
                "Error accessing location: User denied Geolocation. [ERR001]"
              )}`
          );
          setIsLocationPermissionAllowed(false);
          console.error(
            "Error accessing location: User denied Geolocation. [ERR001]:"
          );
        }
      );
    } catch (ex) {
      console.log("Error in requestPermission", ex);
    }
  };

  useEffect(() => {
    checkMediaPermission();
  }, []);

  return (
    <Suspense fallback="loading">
      <div className="flex flex-1 w-full">
        <div className="flex flex-col flex-1 py-3 px-3 lg:py-10 lg:px-8 xl:px-14">
          <div
            style={{
              backgroundImage: !isMobileOrTab
                ? "linear-gradient(360deg, rgba(107, 107, 184, 0) 81.6%, rgba(107, 107, 184, 0.1) 100%)"
                : "",
            }}
            className="flex flex-col lg:w-3/5 xl:w-1/2 h-full justify-between lg:border lg:border-gray-275 rounded-xl lg:p-5"
          >
            <div className="w-full mt-0">
              <p className="hidden lg:block text-4xl text-black-150 font-extrabold mb-0">
                {t("Start Your Journey")}
              </p>
              <div className="lg:mt-14">
                <label htmlFor="referenceNo" className="block text-gray-150">
                  {t("Reference No.")}
                </label>
                <div className="px-4 py-2 mt-3 border border-gray-275 rounded-md focus:outline-none bg-gray-100 w-full">
                  <p className="text-black-150">{refNo}</p>
                </div>
                <label
                  htmlFor="language"
                  className="block text-gray-150 mt-6 mb-3"
                >
                  {t("Language")}
                </label>
                <select
                  id="language"
                  className="border border-gray-275 rounded-md focus:outline-none bg-gray-100 w-full p-2"
                  value={lang}
                  onChange={handleLanguageChange}
                >
                  <option>Choose language</option>
                  {supportedLanguages ? (
                    supportedLanguages.map((lang, i) => {
                      return (
                        <option key={`${lang}`} value={lang}>
                          {languageNativeMap[lang]}
                        </option>
                      );
                    })
                  ) : (
                    <option>Choose language</option>
                  )}
                </select>
                {err && (
                  <p className="text-red-500 text-sm mt-1">
                    Please select language
                  </p>
                )}
              </div>
            </div>
            <div className="border-t border-darkGray-100">
              <p className="text-black-150 text-sm italic mt-3 mb-6">
                "
                {t(
                  'I hereby give my consent and authorize ICICI Prudential Life Insurance Company by clicking on "Start Your Journey" button to use my photo and video footage for verification purpose and to share my photo and video footage provided herein on confidential basis with ICICI group and/or third-party agencies'
                )}
                "
              </p>
              <button
                onClick={async () => {
                  if (
                    isCameraPermissionAllowed &&
                    isMicrophonePermissionAllowed &&
                    isLocationPermissionAllowed &&
                    mediaError === null
                  ) {
                    const isDeviceDataValid = !(
                      !latitude ||
                      latitude === undefined ||
                      !longitude ||
                      longitude === undefined ||
                      !browserName ||
                      !deviceName ||
                      !network
                    );

                    if (!isDeviceDataValid) {
                      setOpenErrorBox(true);
                    } else {
                      const isValid = handleValidation({
                        lang,
                      });
                      if (isValid) {
                        setIsStartingJourneyClick(true);
                        const isValidStartJourney = await startJourney();
                        if (isValidStartJourney) {
                          joinMeeting();
                        } else {
                          setIsStartingJourneyClick(false)
                        }
                      }
                    }
                  } else {
                    checkMediaPermission();
                  }
                }}
                className="w-full lg:max-w-max bg-orange-150 disabled:bg-opacity-15 font-semibold text-white py-2 px-4 rounded-md disabled:cursor-not-allowed"
                disabled={
                  allDetails?.currentJourneyIndex > 15 ||
                  loadData ||
                  isStartingJourneyClick ||
                  error ||
                  mediaError ||
                  encryptedText === "null" || !refNo
                }
              >
                {isStartingJourneyClick ? (
                  <p className="flex flex-row items-center justify-center">
                    <svg
                      aria-hidden="true"
                      class="w-7 h-7 mr-2 text-orange-150 animate-spin dark:text-orange-150 fill-white"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                    {t("Starting Your Journey...")}
                  </p>
                ) : (
                  t("Start Your Journey")
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {open && (
        <ConfirmBox
          open={open}
          setOpen={setOpen}
          text={mediaError ? mediaError : errMsg}
          buttonText={isDifferentClose ? "Close" : t("Close")}
          buttonHandler={() => {
            if (isDifferentClose) {
              setOpen(false);
              window.location.replace("https://www.iciciprulife.com/");
            } else {
              setOpen(false);
            }
          }}
        />
      )}
      {openErrorBox && (
        <ConfirmBox
          open={openErrorBox}
          setOpen={setOpenErrorBox}
          text={t(
            "Error accessing location: User denied Geolocation. [ERR001]"
          )}
          buttonText={t("Close")}
        />
      )}
    </Suspense>
  );
};

export default Intropage;
